import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  ButtonLink,
  cn,
  ComboBox,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  DialogTitle,
  Item,
  RiAccountCircleLine,
  Spinner,
  Stack,
  toast,
} from 'component-library';
import { FC, PropsWithChildren } from 'react';
import { FormProvider, SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RestApiBadRequestServerError } from '@/api/rest/resources/errors/RestApiBadRequestError';
import { PostOrganizationLimitedAccessOrganizationRequestBodyData } from '@/api/rest/resources/types/limitedAccessOrganization';
import { PlotGroup } from '@/api/rest/resources/types/plotGroup';
import { User } from '@/api/rest/resources/types/user';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  SuspenseBoundary,
  useFormField,
} from '@/components';
import { parsePlotGroupBodyData } from '@/components/PlotGroups/utils/parsePlotGroupBodyData';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { usePathSet } from '@/hooks/usePathSet';
import { useAuth } from '@/lib/auth';
import { Logger } from '@/lib/logs/logger';
import { useUsers } from '@/pages/shared/account/pages/team/hooks/useUsers';
import { useOrganizationPlotGroups } from '@/pages/shared/hooks/useOrganizationPlotGroup';
import { isNullish } from '@/utils/booleans';

import {
  useCreateLimitedAccessOrganization,
  useLimitedAccessOrganization,
} from '../../hooks/useLimitedAccessOrganization';

export type EditLimitedAccessOrganizationProps = {
  organizationId: string;
  open: boolean;
  onOpenChange: DialogProps['onOpenChange'];
  plotGroups?: PlotGroup[];
  users?: User[];
};

export const EditLimitedAccessOrganization = ({
  open,
  onOpenChange,
  organizationId,
  plotGroups: plotGroupsArg,
  users: usersArg,
}: EditLimitedAccessOrganizationProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const plotGroupBodies = useOrganizationPlotGroups(organizationId).data;
  const plotGroups: PlotGroup[] = plotGroupsArg ?? plotGroupBodies.map((body) => parsePlotGroupBodyData(body));
  const allUsers = useUsers(organizationId).data;
  const users = usersArg ?? allUsers;

  const onSuccess = () => {
    onOpenChange?.(false);
    queryClient.invalidateQueries({
      queryKey: ['organization', { organization_id: organizationId }, 'limited-access-organization'],
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='w-fit max-w-fit'>
        <DialogHeader>
          <DialogTitle>
            {t('global.organization.limitedAccessOrganization.createLimitedAccessOrganization.title')}
          </DialogTitle>
        </DialogHeader>
        <SuspenseBoundary
          fallback={
            <Stack centerMain>
              <Spinner size={10} />
            </Stack>
          }
        >
          <Form onSuccess={onSuccess} organizationId={organizationId} plotGroups={plotGroups} users={users} />
        </SuspenseBoundary>
        {plotGroups.length === 1 && !isNullish(plotGroups.at(0)) && (
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          <ExistingLimitedAccessUsers organizationId={organizationId} plotGroup={plotGroups.at(0)!} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export type EditLimitedAccessOrganizationFields = PostOrganizationLimitedAccessOrganizationRequestBodyData;

const Form = ({
  onSuccess,
  organizationId,
  plotGroups,
  users,
}: {
  onSuccess: () => void;
  organizationId: string;
  plotGroups: PlotGroup[];
  users: User[];
}) => {
  const { t } = useTranslation();
  const paths = usePathSet();

  const form = useForm<EditLimitedAccessOrganizationFields>({
    defaultValues: {
      plot_group_ids: [],
      user_ids: [],
    },
  });

  const isFormPristine = !form.formState.isDirty;

  const { submit, isSubmitting } = useCreateLimitedAccessOrganization();

  const onSubmit: SubmitHandler<EditLimitedAccessOrganizationFields> = async (inputs) => {
    try {
      form.clearErrors();
      await submit(organizationId, inputs.plot_group_ids ?? [], inputs.user_ids ?? []);
      onSuccess();
      form.reset();
    } catch (error) {
      if (error instanceof Error) {
        Logger.error(error);
      }
      if (error instanceof RestApiBadRequestServerError && error.formErrors.length > 0) {
        error.formErrors.forEach((formError) => {
          form.setError(formError.field as keyof EditLimitedAccessOrganizationFields, {
            type: 'custom',
            message: formError.messages?.[0],
          });
        });
      } else {
        toast({
          title: t('global.ui.toast.errorToastFallbackTitle'),
          description: t('global.ui.toast.errorToastFallbackDescription'),
          type: 'error',
        });
      }
    }
  };

  return (
    <FormProvider {...form}>
      <DialogBody>
        <form
          id='edit-limited-access-plot-group-form'
          onSubmit={form.handleSubmit(onSubmit)}
          data-testid='edit-limited-access-plot-group-form'
        >
          <fieldset disabled={isSubmitting}>
            <Stack spacing={8}>
              <PlotGroups plotGroups={plotGroups} form={form} />
              <Users users={users} form={form} />
            </Stack>
          </fieldset>
        </form>
      </DialogBody>
      <DialogFooter className='mt-12'>
        <DialogClose asChild>
          <Button variant='text' size='small' disabled={isSubmitting}>
            {t('global.ui.buttons.cancel')}
          </Button>
        </DialogClose>
        <Stack direction='row' spacing={4}>
          <ButtonLink
            size='small'
            variant='outline'
            to={paths.accountTeamInvite}
            leftAdornment={<Icon icon='ri-account-circle-line' className=' text-primary-100' />}
          >
            {t('global.organization.limitedAccessOrganization.createLimitedAccessOrganization.inviteNewUser')}
          </ButtonLink>
          <Button
            form='edit-limited-access-plot-group-form'
            size='small'
            type='submit'
            disabled={isSubmitting || isFormPristine}
            loading={isSubmitting}
          >
            {t('global.ui.buttons.save')}
          </Button>
        </Stack>
      </DialogFooter>
    </FormProvider>
  );
};

export const PlotGroups = ({
  plotGroups,
  form,
  required = true,
}: {
  plotGroups: PlotGroup[];
  form: UseFormReturn<EditLimitedAccessOrganizationFields>;
  required?: boolean;
}) => {
  const { t } = useTranslation();

  const plotGroupItems: Item[] = plotGroups
    .filter((plotGroup) => !isNullish(plotGroup.id) && !isNullish(plotGroup.name))
    .map((plotGroup) => {
      return {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        value: plotGroup.id!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        label: plotGroup.name!,
      };
    });
  return (
    <FormField
      name='plot_group_ids'
      control={form.control}
      rules={{
        required: required ? t('global.ui.form.input.required') : '',
      }}
      render={({ field }) => (
        <FormItem className='flex before:typography-overline before:text-text-secondary'>
          <Stack direction='row' className={cn('w-full flex-col items-start')}>
            <CustomFormLabel>
              {t('global.organization.limitedAccessOrganization.createLimitedAccessOrganization.form.plotGroups.label')}
              {required ? '*' : ''}
            </CustomFormLabel>
            <div className='lg:w-[800px]'>
              <FormControl>
                <ComboBox
                  data-testid='plot-groups-combobox'
                  key='plot-groups-combobox'
                  {...field}
                  data={plotGroupItems}
                  initialSelectedItems={
                    plotGroupItems.length === 1 && !isNullish(plotGroupItems[0]) ? [plotGroupItems[0]] : []
                  }
                  placeholder={t(
                    'global.organization.limitedAccessOrganization.createLimitedAccessOrganization.form.plotGroups.placeholder',
                  )}
                />
              </FormControl>
              <CustomFormMessage>{form.formState.errors.plot_group_ids?.message}</CustomFormMessage>
              <FormDescription>
                {t(
                  'global.organization.limitedAccessOrganization.createLimitedAccessOrganization.form.plotGroups.helper',
                )}
              </FormDescription>
            </div>
          </Stack>
        </FormItem>
      )}
    />
  );
};

const Users = ({ users, form }: { users: User[]; form: UseFormReturn<EditLimitedAccessOrganizationFields> }) => {
  const { t } = useTranslation();
  const { authenticatedUser } = useAuth();

  const userItems: Item[] = users
    .filter((user) => !isNullish(user.id) && !isNullish(user.email) && user.id !== authenticatedUser.user?.id)
    .map((user) => {
      return {
        value: user.id,
        label: user.email,
      };
    });

  return (
    <FormField
      name='user_ids'
      control={form.control}
      rules={{
        required: t('global.ui.form.input.required'),
      }}
      render={({ field }) => (
        <FormItem className='flex before:typography-overline before:text-text-secondary'>
          <Stack direction='row' className={cn('w-full flex-col items-start')}>
            <CustomFormLabel>
              {t('global.organization.limitedAccessOrganization.createLimitedAccessOrganization.form.users.label')}*
            </CustomFormLabel>
            <div className='lg:w-[800px]'>
              <FormControl>
                <ComboBox
                  data-testid='users-combobox'
                  key='users-combobox'
                  {...field}
                  data={userItems}
                  placeholder={t(
                    'global.organization.limitedAccessOrganization.createLimitedAccessOrganization.form.users.placeholder',
                  )}
                />
              </FormControl>
              <CustomFormMessage>{form.formState.errors.user_ids?.message}</CustomFormMessage>
              <FormDescription>
                {t('global.organization.limitedAccessOrganization.createLimitedAccessOrganization.form.users.helper')}
              </FormDescription>
            </div>
          </Stack>
        </FormItem>
      )}
    />
  );
};

const CustomFormLabel: FC<PropsWithChildren> = ({ children }) => {
  const { error } = useFormField();

  return (
    <FormLabel
      className={cn('text-[.75rem] font-medium leading-4 text-text-secondary md:text-[.8125rem] md:leading-5', {
        'text-error': error,
      })}
    >
      {children}
    </FormLabel>
  );
};

const CustomFormMessage: FC<PropsWithChildren> = ({ children }) => (
  <FormMessage className='typography-inputLabel mt-0 text-error'>{children}</FormMessage>
);

const ExistingLimitedAccessUsers = ({
  organizationId,
  plotGroup,
}: {
  organizationId: string;
  plotGroup: PlotGroup;
}) => {
  const { t } = useTranslation();
  const limitedAccessOrganizations = useLimitedAccessOrganization(organizationId, {
    plot_group_id: plotGroup.id ?? '',
  }).data;

  if (isNullish(limitedAccessOrganizations) || limitedAccessOrganizations.length === 0) return null;

  const users = limitedAccessOrganizations.filter((lao) => lao.plot_group.id === plotGroup.id).map((lao) => lao.user);
  if (users.length === 0) return null;
  return (
    <Stack className='gap-2 mt-12 border-t border-divider pt-8 lg:w-[800px]'>
      <span className='typography-h3 pb-4'>
        {t('global.organization.limitedAccessOrganization.createLimitedAccessOrganization.currentAccesses')}
      </span>
      {users.map((user) => (
        <Stack key={user.id} className='grid grid-cols-2 p-2 typography-body1'>
          <Stack className='flex flex-row gap-2'>
            <RiAccountCircleLine className='text-text-secondary text-[24px] self-center' />
            <Stack>
              <span className='typography-body1 text-text-primary truncate max-w-[250px]'>
                {user.firstname} {user.lastname}
              </span>
              <span className='typography-body1 text-text-secondary truncate max-w-[250px]'>{user.email}</span>
            </Stack>
          </Stack>
          <Stack>
            <span className='typography-body1 text-text-primary'>
              {t('global.organization.limitedAccessOrganization.createLimitedAccessOrganization.accessRights')}
            </span>
            <span className='typography-body1 text-text-secondary'>
              {t(`global.memberRoles.${user.membership.role}`)}
            </span>
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
};
